import i18n from 'shared/lib/i18n';

import ScheduleComponent from '../components/ScheduleComponent';

import { toSentenceCase } from '../../../utils';
import { formatTimeRange, getIsAnyTime, getMomentDateAndTime } from '../utils';

import {
  DAYS,
  DEFAULT_END_ANY_TIME,
  DEFAULT_END_TIME,
  DEFAULT_START_TIME,
} from '../constants';

const generateAllDaysAnyTime = () => {
  const allDaysAnyTime = {};
  DAYS.forEach((day) => {
    allDaysAnyTime[day] = [
      { startTime: DEFAULT_START_TIME, endTime: DEFAULT_END_ANY_TIME },
    ];
  });
  return allDaysAnyTime;
};

const getEveryDayData = (schedule, onChange, getDisabledScheduleValues) => {
  const onCheckboxChange = (event) => {
    const value = event.target.checked;
    const prevEverydayValues = schedule?.everyday || {};
    const daySchedule =
      !value && prevEverydayValues?.isOn ? generateAllDaysAnyTime() : {};
    const newSchedule = {
      ...daySchedule,
      everyday: {
        isOn: value,
        timeSlots: value
          ? [{ startTime: DEFAULT_START_TIME, endTime: DEFAULT_END_ANY_TIME }]
          : [{}],
      },
    };
    return onChange(newSchedule);
  };

  const onRadioChange = (event) => {
    const { value } = event.target;
    const newValue =
      value === 'any'
        ? [{ startTime: DEFAULT_START_TIME, endTime: DEFAULT_END_ANY_TIME }]
        : [{ startTime: DEFAULT_START_TIME, endTime: DEFAULT_END_TIME }];
    const prevEverydayValues = schedule?.everyday || {};
    const newSchedule = {
      ...schedule,
      everyday: { ...prevEverydayValues, timeSlots: newValue },
    };
    return onChange(newSchedule);
  };

  const onTimeChange = (date, day, type, index) => {
    let [, time] = getMomentDateAndTime(date);

    // replace seconds value with '00'
    // check if a user has selected 12:00:00 am of the next day ->
    // replace with 23:59:00 pm (due to the one day limit and 15-min intervals)
    time = `${time.substring(0, 5)}:00`;
    if (type === 'endTime' && time === DEFAULT_START_TIME) {
      time = `${DEFAULT_END_ANY_TIME.substring(0, 5)}:00`;
    }
    const daySchedule = [...schedule.everyday.timeSlots];
    const selectedTimeInterval = daySchedule.splice(index, 1);
    const updatedTimeInterval = { ...selectedTimeInterval[0], [type]: time };
    daySchedule.splice(index, 0, updatedTimeInterval);

    const prevEverydayValues = schedule?.everyday || {};
    const newSchedule = {
      ...schedule,
      everyday: { ...prevEverydayValues, timeSlots: daySchedule },
    };
    return onChange(newSchedule);
  };

  const onStartTimeChange = (date, day, index) =>
    onTimeChange(date, day, 'startTime', index);
  const onEndTimeChange = (date, day, index) =>
    onTimeChange(date, day, 'endTime', index);

  const onAddTimeInterval = () => {
    const daySchedule = [...schedule.everyday.timeSlots, {}];
    const newSchedule = {
      ...schedule,
      everyday: { ...schedule.everyday, timeSlots: daySchedule },
    };
    return onChange(newSchedule);
  };

  const onRemoveTimeInterval = (day, index) => {
    const daySchedule = [...schedule.everyday.timeSlots];
    daySchedule.splice(index, 1);
    const newSchedule = {
      ...schedule,
      everyday: { ...schedule.everyday, timeSlots: daySchedule },
    };
    return onChange(newSchedule);
  };

  const { isOn: isEveryday, timeSlots } = schedule?.everyday || {};
  const name = 'everyday';
  const isAnyTime = getIsAnyTime(
    timeSlots?.[0]?.startTime,
    timeSlots?.[0]?.endTime,
  );
  const radioValue = !isEveryday ? null : isAnyTime ? 'any' : 'range';
  const isTimeDisabled = !isEveryday || isAnyTime;
  return {
    checkbox: {
      name,
      value: !!isEveryday,
      onChange: onCheckboxChange,
      label: i18n.t('schedule.everyDay'),
    },
    radio: {
      name,
      value: radioValue,
      onChange: onRadioChange,
      disabled: !isEveryday,
    },
    startTime: timeSlots?.map(({ startTime, endTime }) => ({
      name,
      value: formatTimeRange(startTime),
      onChange: onStartTimeChange,
      disabled: isTimeDisabled,
      getDisabledHours: () =>
        getDisabledScheduleValues('hour', 'startTime', startTime, endTime),
      getDisabledMinutes: (selectedHour) =>
        getDisabledScheduleValues(
          'minute',
          'startTime',
          startTime,
          endTime,
          selectedHour,
        ),
    })),
    endTime: timeSlots?.map(({ startTime, endTime }) => ({
      name,
      value: formatTimeRange(endTime),
      onChange: onEndTimeChange,
      onAddTimeInterval,
      onRemoveTimeInterval,
      disabled: isTimeDisabled,
      getDisabledHours: () =>
        getDisabledScheduleValues('hour', 'endTime', startTime, endTime),
      getDisabledMinutes: (selectedHour) =>
        getDisabledScheduleValues(
          'minute',
          'endTime',
          startTime,
          endTime,
          selectedHour,
        ),
    })),
  };
};

const getDayData = (schedule, name, onFieldChange) => {
  const {
    onCheckboxChange,
    onRadioChange,
    onStartTimeChange,
    onEndTimeChange,
    onAddTimeInterval,
    onRemoveTimeInterval,
    getDisabledScheduleValues,
  } = onFieldChange;
  const day = schedule?.[name] || [{}];
  const isEveryDaySelected = schedule?.everyday?.isOn;
  const { startTime, endTime } = day?.[0] || {};
  const isDaySelected = startTime && endTime;
  const isAnyTime = getIsAnyTime(startTime, endTime);
  const radioValue = !isDaySelected ? null : isAnyTime ? 'any' : 'range';
  const isTimeDisabled = !isDaySelected || isAnyTime;
  const i18nDayKey = `days.${name}`;
  return {
    checkbox: {
      name,
      value: !!isDaySelected,
      onChange: onCheckboxChange,
      label: toSentenceCase(i18n.t(i18nDayKey)),
      disabled: isEveryDaySelected,
    },
    radio: {
      name,
      value: radioValue,
      onChange: onRadioChange,
      disabled: !isDaySelected || isEveryDaySelected,
    },
    startTime: day?.map(({ startTime, endTime }) => ({
      name,
      value: formatTimeRange(startTime),
      onChange: onStartTimeChange,
      disabled: isTimeDisabled || isEveryDaySelected,
      getDisabledHours: () =>
        getDisabledScheduleValues('hour', 'startTime', startTime, endTime),
      getDisabledMinutes: (selectedHour) =>
        getDisabledScheduleValues(
          'minute',
          'startTime',
          startTime,
          endTime,
          selectedHour,
        ),
    })),
    endTime: day?.map(({ startTime, endTime }) => ({
      name,
      value: formatTimeRange(endTime),
      onChange: onEndTimeChange,
      onAddTimeInterval,
      onRemoveTimeInterval,
      disabled: isTimeDisabled || isEveryDaySelected,
      getDisabledHours: () =>
        getDisabledScheduleValues('hour', 'endTime', startTime, endTime),
      getDisabledMinutes: (selectedHour) =>
        getDisabledScheduleValues(
          'minute',
          'endTime',
          startTime,
          endTime,
          selectedHour,
        ),
    })),
  };
};

const getScheduleValues = (schedule, onChange, getDisabledScheduleValues) => {
  const onCheckboxChange = (event) => {
    const day = event.target.name;
    const value = event.target.checked;
    const newDayValue = value
      ? [{ startTime: DEFAULT_START_TIME, endTime: DEFAULT_END_ANY_TIME }]
      : [{}];
    const newSchedule = { ...schedule, [day]: newDayValue };
    return onChange(newSchedule);
  };

  const onRadioChange = (event) => {
    const day = event.target.name;
    const { value } = event.target;
    const newDayValue =
      value === 'any'
        ? [{ startTime: DEFAULT_START_TIME, endTime: DEFAULT_END_ANY_TIME }]
        : [{ startTime: DEFAULT_START_TIME, endTime: DEFAULT_END_TIME }];
    const newSchedule = { ...schedule, [day]: newDayValue };
    return onChange(newSchedule);
  };

  const onTimeChange = (date, day, type, index) => {
    let [, time] = getMomentDateAndTime(date);

    // replace seconds value with '00'
    // check if a user has selected 12:00:00 am of the next day ->
    // replace with 23:59:00 pm (due to the one day limit and 15-min intervals)
    time = `${time.substring(0, 5)}:00`;
    if (type === 'endTime' && time === DEFAULT_START_TIME) {
      time = `${DEFAULT_END_ANY_TIME.substring(0, 5)}:00`;
    }

    const daySchedule = [...schedule[day]];
    const selectedTimeInterval = daySchedule.splice(index, 1);
    const updatedTimeInterval = { ...selectedTimeInterval[0], [type]: time };
    daySchedule.splice(index, 0, updatedTimeInterval);
    const newSchedule = {
      ...schedule,
      [day]: daySchedule,
    };
    return onChange(newSchedule);
  };

  const onStartTimeChange = (date, day, index) =>
    onTimeChange(date, day, 'startTime', index);
  const onEndTimeChange = (date, day, index) =>
    onTimeChange(date, day, 'endTime', index);

  const onAddTimeInterval = (day) => {
    const daySchedule = [...schedule[day], {}];
    const newSchedule = {
      ...schedule,
      [day]: daySchedule,
    };
    return onChange(newSchedule);
  };

  const onRemoveTimeInterval = (day, index) => {
    const daySchedule = [...schedule[day]];
    daySchedule.splice(index, 1);
    const newSchedule = {
      ...schedule,
      [day]: daySchedule,
    };
    return onChange(newSchedule);
  };

  const onFieldChange = {
    onCheckboxChange,
    onRadioChange,
    onStartTimeChange,
    onEndTimeChange,
    onAddTimeInterval,
    onRemoveTimeInterval,
    getDisabledScheduleValues,
  };

  return [
    getEveryDayData(schedule, onChange, getDisabledScheduleValues),
    getDayData(schedule, 'monday', onFieldChange),
    getDayData(schedule, 'tuesday', onFieldChange),
    getDayData(schedule, 'wednesday', onFieldChange),
    getDayData(schedule, 'thursday', onFieldChange),
    getDayData(schedule, 'friday', onFieldChange),
    getDayData(schedule, 'saturday', onFieldChange),
    getDayData(schedule, 'sunday', onFieldChange),
  ];
};

const ScheduleContainer = (props) => (
  <ScheduleComponent {...props} getScheduleValues={getScheduleValues} />
);

export default ScheduleContainer;
